import type { Variant } from '@/types/Product';
import { stripTierSuffix } from './variantRef';

/**
 * Résolution d'une variante e-commerce à partir des options retenues — logique PURE.
 *
 * Extrait à l'identique du getter `productStore.selectedVariant`, avec UN ajout :
 * la couleur visée peut être FORCÉE (`colorName`). C'est ce qui permet de résoudre
 * la variante d'un coloris AUTRE que celui actuellement prévisualisé — le cœur du
 * mode multi-variantes, où une même création part sur plusieurs coloris à la fois.
 *
 * Module SANS store ni DOM, pour rester testable (cf. variantResolution.spec.ts) :
 * c'est le pattern du projet — extraire la logique, tester l'extrait.
 */

export interface VariantResolutionInput {
    variants: Variant[];
    /**
     * Base (variantRef sans suffixe de palier) du MODÈLE sélectionné. Sur un
     * produit « thème », plusieurs modèles partagent les mêmes options et seule la
     * base les discrimine. `null` = pas de thème.
     */
    baseRef?: string | null;
    /** Options retenues (quantité, couleur, matière…). */
    options: Record<string, string>;
    /** Nom de l'option couleur e-commerce. Absent = produit sans axe couleur. */
    colorOptionName?: string | null;
    /** Coloris visé, quand il diffère de celui porté par `options`. */
    colorName?: string | null;
}

/** Options effectives : `options`, avec le coloris forcé par-dessus quand demandé. */
function effectiveOptions(input: VariantResolutionInput): Record<string, string> {
    const { options, colorOptionName, colorName } = input;
    if (!colorOptionName || !colorName) {
        return options;
    }
    return { ...options, [colorOptionName]: colorName };
}

/**
 * Variante dont TOUTES les options sélectionnées correspondent, `null` sinon.
 *
 * Le passage par la base d'abord n'est pas un détail : un `find` par options seul
 * renvoie le 1er modèle de la liste sur un produit « thème » — donc le mauvais
 * prix et le mauvais produit au panier.
 */
export function resolveVariantFor(input: VariantResolutionInput): Variant | null {
    const variants = input.variants ?? [];
    const options = effectiveOptions(input);
    const matchesOptions = (variant: Variant) =>
        Object.entries(options).every(([key, value]) => variant.options?.[key] === value);

    if (input.baseRef) {
        const inBase = variants.find(
            (variant) => stripTierSuffix(variant.variantRef) === input.baseRef && matchesOptions(variant)
        );
        if (inBase) {
            return inBase;
        }
    }

    return variants.find(matchesOptions) ?? null;
}
